/**
 * Utility to convert relative media URLs to absolute URLs
 */

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Fallback: Use the same hostname as the web app
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = process.env.REACT_APP_API_PORT || '8000';
  return `${protocol}//${hostname}:${port}`;
};

const API_URL = getApiUrl();

const encodeRelativePath = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const hashIndex = normalizedPath.indexOf('#');
  const hash = hashIndex >= 0 ? normalizedPath.slice(hashIndex) : '';
  const pathWithoutHash = hashIndex >= 0 ? normalizedPath.slice(0, hashIndex) : normalizedPath;
  const queryIndex = pathWithoutHash.indexOf('?');
  const query = queryIndex >= 0 ? pathWithoutHash.slice(queryIndex) : '';
  const pathname = queryIndex >= 0 ? pathWithoutHash.slice(0, queryIndex) : pathWithoutHash;

  const encodedPathname = pathname
    .split('/')
    .map((segment) => {
      if (!segment) return segment;

      try {
        return encodeURIComponent(decodeURIComponent(segment));
      } catch {
        return encodeURIComponent(segment);
      }
    })
    .join('/');

  return `${encodedPathname}${query}${hash}`;
};

/**
 * Convert a relative media URL to an absolute URL
 * @param url - The media URL (can be relative or absolute)
 * @returns Absolute URL
 */
export const getMediaUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  
  // If data URL (base64 encoded image), return as is
  if (url.startsWith('data:')) {
    return url;
  }
  
  // If already absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `${API_URL}${encodeRelativePath(url)}`;
};

/**
 * Get absolute image URL
 */
export const getImageUrl = (imageUrl: string | null | undefined): string | null => {
  return getMediaUrl(imageUrl);
};

/**
 * Get absolute video URL
 */
export const getVideoUrl = (videoUrl: string | null | undefined): string | null => {
  return getMediaUrl(videoUrl);
};
